import { Button, DatePicker, Modal, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CellRenderInfo } from "@rc-component/picker/lib/interface";
import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CurrencySymbol from "@/Components/Common/CurrencySymbol/CurrencySymbol";
import "../../Components/Feature/Calendar/Calendar.css";
import "../../Components/HyperSales/SaleCard.css";
import { _eventBus } from "../../core/events/EventBus";
import { isEmpty } from "../../Lib/Utils/validationUtil";
import { bookItAgainTree } from "../../Orchestrator/ConstantTree/BookItAgain/BookItAgainTree";
import {
  updateRecoilState,
  useRecState,
} from "../../Orchestrator/Recoils/Events/Recoil";
import {
  IBestPricePrices,
  IBookedTrips,
  IBooking,
  IIsDateOfferCheckDoneAtom,
  ISelectedCalenderDatePrice,
  ISelectedDisabledCalenderDate,
} from "../../Orchestrator/Types/Index";
import TripItem from "../HyperSales/TripItem";
import { TripTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import enLocale from "antd/es/date-picker/locale/en_GB";
import arLocale from "antd/es/date-picker/locale/ar_EG";

const { RangePicker } = DatePicker;

const BookItAgainDateSelection: React.FC = () => {
  const intl = useIntl();
  const [isModalVisible] = useRecState<boolean>("isModalVisibleAtom");
  const [isRoundTripModalVisible] = useRecState<boolean>(
    "isRoundTripModalVisibleAtom"
  );
  const [isRoundTripInboundModalVisible] = useRecState<boolean>(
    "isRoundTripInboundModalVisibleAtom"
  );
  const [dateSelectedFromCalendar] = useRecState<boolean>(
    "dateSelectedFromCalendarAtom"
  );
  const [selectedBookingForBookItAgain] = useRecState<IBooking>(
    "selectedBookingForBookItAgainAtom"
  );
  const [selectedRange] = useRecState<[Dayjs | null, Dayjs | null]>(
    "selectedDateRangeStateAtom"
  );
  const [markSelectedDateAsDisable] =
    useRecState<ISelectedDisabledCalenderDate>("markSelectedDateAsDisableAtom");
  const [markSelectedDateAsSoldOut] =
    useRecState<ISelectedDisabledCalenderDate>("markSelectedDateAsSoldOutAtom");
  const [isDateOfferCheckDone] = useRecState<IIsDateOfferCheckDoneAtom>(
    "isDateOfferCheckDoneAtom"
  );
  const [selectedDatePrice] = useRecState<ISelectedCalenderDatePrice[]>(
    "selectedDatePriceAtom"
  );

  const [selectedTrip] = useRecState<IBookedTrips>(
    "selectedTripForBookItAgainAtom"
  );

  const [calendarDayInterval] = useRecState<number>("calendarDayIntervalAtom");
  const [booking] = useRecState<number>("selectedBookingForBookItAgainAtom");

  const [rangePickerKey, setRangePickerKey] = useState(0);
  const [currentlySelectedDate, setCurrentlySelectedDate] =
    useState<Dayjs | null>(null);

  useEffect(() => {
    if (dateSelectedFromCalendar && selectedRange[0]) {
      if (isRoundTripInboundModalVisible) {
        if (selectedRange[1] == null) return;
      }
      const direction = isRoundTripInboundModalVisible
        ? TripTypeEnum.IN_BOUND
        : TripTypeEnum.OUT_BOUND;
      const tripType = isModalVisible ? "oneWayTrip" : "roundTrip";

      _eventBus.emit({
        eventName: bookItAgainTree.Date.Click,
        data: {
          selectedTrip,
          selectedRange: selectedRange,
          direction: direction,
          tripType: tripType,
          selectedDatePrice,
          markSelectedDateAsDisable,
          markSelectedDateAsSoldOut,
          isDateOfferCheckDone,
          selectedBookingForBookItAgain,
          booking,
        },
      });
    }
  }, [dateSelectedFromCalendar, selectedRange[0], selectedRange[1]]);

  const handleDateChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0]) {
      if (dates[0]?.isSame(dayjs(), "day")) {
        /**
         * Set dates[0] to current date if it is today
         * This is to ensure that the date is set to today when the user selects it.
         * This is important because the date picker may not always set the date to today from 00:00:00.000
         * We have timezone issue in Dubai, so we need to set the date to today.
         * Because 00:00:00.000 is not same as today date in Dubai timezone.
         * So we need to set the date to today.
         * */
        dates[0] = dayjs();
      }
    }

    // Add selected date logic for message display
    let selectedDate: Dayjs | null = null;
    if (dates) {
      // For round trip inbound selection, the date might be at index 1
      if (isRoundTripInboundModalVisible && dates[1]) {
        selectedDate = dates[1];
      } else if (dates[0]) {
        selectedDate = dates[0];
      }
    }
    setCurrentlySelectedDate(selectedDate);

    _eventBus.emit({
      eventName: bookItAgainTree.Date.Change,
      data: {
        dates: dates,
        selectedRange: selectedRange,
        isRoundTripModalVisible: isRoundTripModalVisible,
      },
    });
  };

  const showInbound = () => {
    _eventBus.emit({
      eventName: bookItAgainTree.Round.OutBound.Continue.Click,
      data: selectedRange[0]?.format("YYYY-MM-DD"),
    });
  };

  const handlePrevious = () => {
    updateRecoilState("selectedDateRangeStateAtom", [selectedRange[0], null]);
    updateRecoilState("isDateOfferCheckDoneAtom", {
      ...isDateOfferCheckDone,
      IN_BOUND: false,
    });
    updateRecoilState("isRoundTripModalVisibleAtom", true);
    updateRecoilState("isRoundTripInboundModalVisibleAtom", false);
  };

  const handleCancel = () => {
    updateRecoilState("selectedDateRangeStateAtom", [null, null]);
    updateRecoilState("calendarStateAtom", {
      selectedDates: [null, null],
      isFilled: false,
    });
    updateRecoilState("isHyperFastStateAtom", false);
    updateRecoilState("isModalVisibleAtom", false);
    updateRecoilState("isRoundTripModalVisibleAtom", false);
    updateRecoilState("isRoundTripInboundModalVisibleAtom", false);

    setRangePickerKey((prevKey) => prevKey + 1);
    setCurrentlySelectedDate(null);

    const element = document.getElementById("details_arrow");
    if (element) {
      element.style.display = "";
    }
  };

  const handleContinue = async () => {
    await _eventBus.emit({
      eventName: bookItAgainTree.Continue.Click,
      data: selectedBookingForBookItAgain,
    });
  };
  const handleInboundContinue = async () => {
    await _eventBus.emit({
      eventName: bookItAgainTree.Round.InBound.Continue.Click,
    });
  };

  const cellRender = useCallback(
    (current: string | number | Dayjs, info: CellRenderInfo<Dayjs>) => {
      if (
        info.type !== "date" ||
        typeof current !== "object" ||
        !("format" in current)
      ) {
        return info.originNode;
      }
      const dateString = current.format("YYYY-MM-DD");
      let price: IBestPricePrices | undefined = undefined;
      if (selectedDatePrice.length > 0) {
        const direction = isRoundTripInboundModalVisible
          ? TripTypeEnum.IN_BOUND
          : TripTypeEnum.OUT_BOUND;
        const dateObject = selectedDatePrice.find((data) => {
          const dateObject = data[direction];
          return dateObject?.date === dateString;
        });
        if (dateObject && !isEmpty(dateObject[direction].date)) {
          if (dateObject[direction].date === dateString) {
            price = {
              currency: "AED",
              date: dateObject[direction].date,
              price: dateObject[direction].price || 0,
            };
          }
        }
      }
      if (price) {
        return (
          <div className="ant-picker-cell-inner">
            <div className="show_date">{current.date()}</div>
            {price != null && (
            <span className="show_price">
              {intl.locale === "ar"
                ? <>{price.price} <CurrencySymbol /></>
                : <><CurrencySymbol /> {price.price}</>}
            </span>
)}
          </div>
        );
      } else {
        if (markSelectedDateAsDisable) {
          const direction = isRoundTripInboundModalVisible
            ? TripTypeEnum.IN_BOUND
            : TripTypeEnum.OUT_BOUND;

          const idDateDisabled = (
            markSelectedDateAsDisable[direction] || []
          ).includes(dateString);
          if (idDateDisabled) {
            return (
              <div className="ant-picker-cell-inner">
                <div className="show_date">{current.date()}</div>
                <span className="show_price">N/A</span>
              </div>
            );
          }
        }
        if (markSelectedDateAsSoldOut) {
          const direction = isRoundTripInboundModalVisible
            ? TripTypeEnum.IN_BOUND
            : TripTypeEnum.OUT_BOUND;
          const idDateDisabled = (
            markSelectedDateAsSoldOut[direction] || []
          ).includes(dateString);
          if (idDateDisabled) {
            return (
              <div className="ant-picker-cell-inner">
                <div className="show_date">{current.date()}</div>
                <span className="show_price">Sold Out</span>
              </div>
            );
          }
        }
      }

      return (
        <div className="ant-picker-cell-inner">
          <div className="show_date">{current.date()}</div>
        </div>
      );
    },
    [
      selectedDatePrice,
      isRoundTripInboundModalVisible,
      markSelectedDateAsDisable,
      markSelectedDateAsSoldOut,
    ]
  );

  const disabledDates = useCallback(
    (currentDate: Dayjs) => {
      const currentDateFormat = currentDate.format("YYYY-MM-DD");
      if (!isRoundTripInboundModalVisible) {
        if (selectedTrip?.departureDateTime) {
          const outboundStart = dayjs(selectedTrip.departureDateTime);
          if (currentDate.isBefore(outboundStart, "day")) {
            return true;
          }
        }
      } else {
        if (selectedRange && selectedRange[0]) {
          const outboundDate = dayjs(selectedRange[0]);
          if (
            currentDate.isBefore(outboundDate, "day") ||
            currentDate.isSame(outboundDate, "day")
          ) {
            return true;
          }
        }
      }
      // const lastDate = dayjs(
      //   calendarBestPrice[calendarBestPrice.length - 1]?.date
      // );
      // const allBestPriceDates = calendarBestPrice.map((d) =>
      //   dayjs(d.date).format("YYYY-MM-DD")
      // );
      // if (
      //   allBestPriceDates.includes(currentDateFormat) &&
      //   (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate))
      // ) {
      //   const hasPrice = calendarBestPrice.some(
      //     (d) => dayjs(d.date).format("YYYY-MM-DD") === currentDateFormat
      //   );
      //   return !hasPrice;
      // }
      if (markSelectedDateAsDisable) {
        const direction = isRoundTripInboundModalVisible
          ? TripTypeEnum.IN_BOUND
          : TripTypeEnum.OUT_BOUND;

        return (markSelectedDateAsDisable[direction] || []).includes(
          currentDateFormat
        );
      }

      return false;
    },
    [markSelectedDateAsDisable, isRoundTripInboundModalVisible, selectedTrip, selectedRange]
  );

  return (
    <div>
      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        closable={true}
        centered={false}
        width={760}
        className="sale-card-modal"
      >
        <Typography.Title
          level={4}
          className="fw-700 text-uppercase ff-wallop fs-24"
        >
          {intl.formatMessage({ id: "selectDateText" })}
        </Typography.Title>

        {selectedTrip && (
          <TripItem
            type={"selected"}
            trip={selectedTrip}
            onShowModal={() => {}}
          />
        )}

        <div className="select-range">
          <RangePicker
            locale={intl.locale === "en" ? enLocale : arLocale}
            open={true}
            value={selectedRange}
            onChange={handleDateChange}
            cellRender={cellRender}
            /**
             * Comment because it's call handleDateChange function two time's.
             */
            // onCalendarChange={handleDateChange}
            format="ddd, DD MMM"
            minDate={selectedTrip?.departureDateTime ? dayjs(selectedTrip.departureDateTime): dayjs()}
            maxDate={dayjs().add(calendarDayInterval, "day")}
            disabled={[false, true]}
            className="range-picker"
            allowEmpty={[false, true]}
            showTime={false}
            popupStyle={{
              display: "block",
            }}
            classNames={{ popup: { root: `change-date-booking home-page-date-picker ${intl.locale === "en" ? "" : "rtl-range-picker"} ` } }}
            disabledDate={disabledDates}
            getPopupContainer={(trigger) => {
              const modalBody = document.querySelector(".select-range");
              return modalBody instanceof HTMLElement
                ? modalBody
                : document.body;
            }}
          />
        </div>

        <div className="range-footer">
          <Typography.Text className="fs-16 fw-600 color-757575 d-flex align-items-center gap-6">
            {/* <InfoCircleOutlined /> */}
            {/* <FormattedMessage id="pricesForOneAdult" /> */}
            {currentlySelectedDate &&
            markSelectedDateAsDisable &&
            (markSelectedDateAsDisable.OUT_BOUND || []).includes(
              currentlySelectedDate.format("YYYY-MM-DD")
            ) ? (
              <FormattedMessage id="noTrainsOnSelctedDate" />
            ) : (
              <FormattedMessage id="pricesForAllPass" />
            )}
          </Typography.Text>
          <Button
            // disabled={selectedRange[0] ? false : true}
            disabled={!isDateOfferCheckDone.OUT_BOUND}
            type="primary"
            className="fs-16 radius-12 border-none"
            onClick={() => handleContinue()}
          >
            <FormattedMessage id="continue" />
          </Button>
        </div>
      </Modal>

      <Modal
        open={isRoundTripModalVisible || isRoundTripInboundModalVisible}
        onCancel={handleCancel}
        footer={null}
        closable={true}
        centered={false}
        width={760}
        className="sale-card-modal"
      >
        <Typography.Title
          level={4}
          className="fw-700 text-uppercase ff-wallop fs-24"
        >
          {isRoundTripModalVisible
            ? "SELECT OUTBOUND DATE"
            : "SELECT INBOUND DATE"}
        </Typography.Title>

        {selectedTrip && isRoundTripModalVisible && (
          <TripItem
            type={"selected"}
            trip={selectedTrip}
            onShowModal={() => {}}
          />
        )}

        {selectedTrip && isRoundTripInboundModalVisible && (
          <TripItem
            type={"selected"}
            trip={selectedTrip}
            isInbound={true}
            onShowModal={() => {}}
          />
        )}

        <div
          className={
            isRoundTripModalVisible
              ? "select-range-one"
              : "select-range-inbound"
          }
        >
          <RangePicker
            locale={intl.locale === "en" ? enLocale : arLocale}
            classNames={{ popup: { root: `change-date-booking home-page-date-picker ${intl.locale === "en" ? "" : "rtl-range-picker"} ` } }}
            key={rangePickerKey}
            open={true}
            value={
              isRoundTripModalVisible ? [selectedRange[0], null] : selectedRange
            }
            /**
             * Comment because it's call handleDateChange function two time's.
             * onChange={handleDateChange}
             */
            onCalendarChange={handleDateChange}
            cellRender={cellRender}
            format="ddd, DD MMM"
            minDate={isRoundTripInboundModalVisible ? dayjs(selectedRange[0]) : selectedTrip?.departureDateTime ? dayjs(selectedTrip.departureDateTime) : dayjs()}
            maxDate={dayjs().add(calendarDayInterval || 0, "day")}
            // maxDate={
            //   isRoundTripInboundModalVisible
            //     ? dayjs().add(calendarDayInterval || 0, "day")
            //     : dayjs(selectedTrip?.inboundData?.arrivalDateTime)
            // }
            className="range-picker"
            showTime={false}
            popupStyle={{
              display: "block",
            }}
            disabledDate={disabledDates}
            getPopupContainer={(trigger) => {
              const modalBody = document.querySelector(
                isRoundTripModalVisible
                  ? ".select-range-one"
                  : ".select-range-inbound"
              );
              return modalBody instanceof HTMLElement
                ? modalBody
                : document.body;
            }}
          />
        </div>

        <div className="range-footer ">
          <Typography.Text className="fs-16 fw-600 color-757575 d-flex align-items-center gap-6">
            {/* <InfoCircleOutlined /> */}
            {(() => {
              if (isRoundTripModalVisible && currentlySelectedDate) {
                // Outbound date selection
                return markSelectedDateAsDisable &&
                  (markSelectedDateAsDisable.OUT_BOUND || []).includes(
                    currentlySelectedDate.format("YYYY-MM-DD")
                  ) ? (
                  <FormattedMessage id="noTrainsOnSelctedDate" />
                ) : (
                  <FormattedMessage id="pricesForAllPass" />
                );
              } else if (
                isRoundTripInboundModalVisible &&
                currentlySelectedDate
              ) {
                // Inbound date selection
                return markSelectedDateAsDisable &&
                  (markSelectedDateAsDisable.IN_BOUND || []).includes(
                    currentlySelectedDate.format("YYYY-MM-DD")
                  ) ? (
                  <FormattedMessage id="noTrainsOnSelctedDate" />
                ) : (
                  <FormattedMessage id="pricesForAllPass" />
                );
              } else {
                // Default message when no date is selected
                return <FormattedMessage id="pricesForAllPass" />;
              }
            })()}
          </Typography.Text>

          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            {isRoundTripModalVisible && (
              <Button
                // disabled={selectedRange[0] ? false : true}
                disabled={!isDateOfferCheckDone.OUT_BOUND}
                type="primary"
                className="fs-16 radius-12 border-none"
                onClick={showInbound}
              >
                <FormattedMessage id="continue" />
              </Button>
            )}

            {isRoundTripInboundModalVisible && (
              <>
                <Button
                  className="fs-16 radius-12 border-none bg-F3E6DD"
                  onClick={handlePrevious}
                >
                  <FormattedMessage id="Previous" />
                </Button>

                <Button
                  // disabled={selectedRange[1] ? false : true}
                  disabled={!isDateOfferCheckDone.IN_BOUND}
                  type="primary"
                  className="fs-16 radius-12 border-none"
                  onClick={() => handleInboundContinue()}
                >
                  <FormattedMessage id="continue" />
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BookItAgainDateSelection;
